<template>
  <div class="jindex-container">
    <!-- 顶部按钮栏 -->
    <div class="top-nav">
      <div class="nav-buttons">
        <el-button
          v-for="(item, index) in navItems"
          :key="index"
          :class="['nav-btn', { active: activeComponent === item.component }]"
          :disabled="shouldDisableButton(item.component)"
          @click="handleButtonClick(item.component)"
        >
          {{ item.label }}
        </el-button>
      </div>
      <div class="action-buttons">
        <span class="file-name-display">{{ displayFileName }}</span>
        <el-button type="primary" size="small" @click="exportProject"
          >导出EXCEL</el-button
        >
        <el-dropdown trigger="click" @command="handleSaveDropdown">
          <el-button type="primary" size="small">
            保存<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="save">保存</el-dropdown-item>
            <el-dropdown-item command="saveAs" v-if="hasSavedBefore"
              >另存为</el-dropdown-item
            >
          </el-dropdown-menu>
        </el-dropdown>
        <el-button type="danger" size="small" @click="closeProject"
          >关闭</el-button
        >
      </div>
    </div>

    <!-- 子组件容器 -->
    <div class="component-container">
      <component :is="currentComponent" ref="dynamicComponent"></component>
    </div>
  </div>
</template>

<script>
import GlobalParams from "./components/GlobalParams.vue";
import InvestmentEstimate from "./components/InvestmentEstimate.vue";
import FundraisingPlan from "./components/FundraisingPlan.vue";
import CostEstimate from "./components/CostEstimate.vue";
import CostExpenses from "./components/CostExpenses.vue";
import WorkingCapital from "./components/WorkingCapital.vue";
import RevenueTax from "./components/RevenueTax.vue";
import ProfitabilityAnalysis from "./components/ProfitabilityAnalysis.vue";
import UncertaintyAnalysis from "./components/UncertaintyAnalysis.vue";
import ExcelExportUtil from "@/utils/excelExport.js";

export default {
  components: {
    GlobalParams,
    InvestmentEstimate,
    FundingPlan: FundraisingPlan,
    CostEstimate,
    CostExpenses,
    WorkingCapital,
    RevenueTax,
    ProfitabilityAnalysis,
    UncertaintyAnalysis,
  },
  data() {
    return {
      activeComponent: "GlobalParams",
      navItems: [
        {
          label: "基础参数设置",
          component: "GlobalParams",
        },
        {
          label: "总投资估算",
          component: "InvestmentEstimate",
        },
        {
          label: "资金筹措和分年投资计划",
          component: "FundingPlan",
        },
        {
          label: "成本费用",
          component: "CostExpenses",
        },

        {
          label: "营业收入税金及利润",
          component: "RevenueTax",
        },
        {
          label: "盈利能力分析",
          component: "ProfitabilityAnalysis",
        },
        {
          label: "不确定性分析",
          component: "UncertaintyAnalysis",
        },
      ],
    };
  },
  computed: {
    currentComponent() {
      return this.activeComponent;
    },
    projectName() {
      return this.$store.state.projectData?.projectName?.trim();
    },
    hasSavedBefore() {
      return !!this.$store.state.saveFileHandle;
    },
    displayFileName() {
      const saveFileHandle = this.$store.state.saveFileHandle;
      if (!saveFileHandle) {
        return "项目文件：未保存";
      }
      let fileName = "";
      if (saveFileHandle.isTraditionalFile) {
        fileName = saveFileHandle.filename || "project.json";
      } else {
        fileName = saveFileHandle.name || "project.json";
      }
      const withoutExt = fileName.replace(/\.json$/i, "");
      let displayName = withoutExt;
      if (displayName.length > 8) {
        displayName = displayName.substring(0, 8) + "...";
      }
      return "项目文件：" + displayName;
    },
  },
  methods: {
    shouldDisableButton(component) {
      if (component === "GlobalParams") {
        return false;
      }
      return !this.projectName;
    },
    handleButtonClick(component) {
      if (component === "GlobalParams") {
        this.switchComponent(component);
        return;
      }
      if (this.projectName) {
        this.switchComponent(component);
      } else {
        this.$toast.warning("请先填写项目名称");
      }
    },
    switchComponent(component) {
      this.activeComponent = component;
    },
    async saveProject() {
      try {
        if (!this.projectName) {
          this.$toast.warning("请先填写项目名称");
          return;
        }

        const saveFileHandle = this.$store.state.saveFileHandle;

        if (!saveFileHandle) {
          if (window.showSaveFilePicker) {
            const fileHandle = await window.showSaveFilePicker({
              suggestedName: `${this.projectName}.json`,
              types: [
                {
                  accept: {
                    "application/json": [".json"],
                  },
                },
              ],
            });
            this.$store.commit("setSaveFileHandle", fileHandle);
            const writable = await fileHandle.createWritable();
            await writable.write(
              JSON.stringify(this.$store.state.projectData, null, 2)
            );
            await writable.close();
            this.$toast.success("保存成功");
          } else {
            const blob = new Blob(
              [JSON.stringify(this.$store.state.projectData, null, 2)],
              {
                type: "application/json",
              }
            );
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${this.projectName}.json`;
            link.click();
            URL.revokeObjectURL(link.href);
            this.$store.commit("setSaveFileHandle", {
              isTraditionalFile: true,
              filename: `${this.projectName}.json`,
            });
            this.$toast.success("保存成功");
          }
        } else {
          if (saveFileHandle.isTraditionalFile) {
            const blob = new Blob(
              [JSON.stringify(this.$store.state.projectData, null, 2)],
              {
                type: "application/json",
              }
            );
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = saveFileHandle.filename || "project.json";
            link.click();
            URL.revokeObjectURL(link.href);
            this.$toast.success("保存成功");
          } else {
            const writable = await saveFileHandle.createWritable();
            await writable.write(
              JSON.stringify(this.$store.state.projectData, null, 2)
            );
            await writable.close();
            this.$toast.success("保存成功");
          }
        }
        this.$store.commit("setIsProjectSaved", true);
      } catch (error) {
        console.error("保存失败:", error);
        if (error.name !== "AbortError") {
          this.$toast.error("保存失败：" + error.message);
        }
      }
    },
    handleSaveDropdown(command) {
      if (command === "save") {
        this.saveProject();
      } else if (command === "saveAs") {
        this.saveAsProject();
      }
    },
    async saveAsProject() {
      try {
        if (!this.projectName) {
          this.$toast.warning("请先填写项目名称");
          return;
        }

        if (window.showSaveFilePicker) {
          // 使用File System Access API
          try {
            const fileHandle = await window.showSaveFilePicker({
              suggestedName: `${this.projectName}.json`,
              types: [
                {
                  accept: {
                    "application/json": [".json"],
                  },
                },
              ],
            });

            // 只有在用户确认覆盖后，才写入文件并更新状态
            const writable = await fileHandle.createWritable();
            await writable.write(
              JSON.stringify(this.$store.state.projectData, null, 2)
            );
            await writable.close();

            // 更新文件句柄和保存状态
            this.$store.commit("setSaveFileHandle", fileHandle);
            this.$store.commit("setIsProjectSaved", true);
            this.$toast.success("另存为成功");
          } catch (saveError) {
            if (saveError.name === "AbortError") {
              // 用户取消了保存操作，不显示错误信息
              return;
            }
            throw saveError;
          }
        } else {
          // 使用传统下载方式
          const blob = new Blob(
            [JSON.stringify(this.$store.state.projectData, null, 2)],
            {
              type: "application/json",
            }
          );
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `${this.projectName}.json`;
          link.click();
          URL.revokeObjectURL(link.href);

          // 更新保存状态，但不更新fileHandle，因为传统下载无法获得文件句柄
          this.$store.commit("setIsProjectSaved", true);
          this.$toast.success("另存为成功");
        }
      } catch (error) {
        console.error("另存为失败:", error);
        if (error.name !== "AbortError") {
          this.$toast.error("另存为失败：" + error.message);
        }
      }
    },

    async closeProject() {
      if (!this.projectName) {
        this.$emit("close");
        return;
      }

      if (this.$store.state.isProjectSaved) {
        this.$emit("close");
        return;
      }

      try {
        const action = await this.$confirm("关闭之前是否保存？", "提示", {
          confirmButtonText: "保存",
          cancelButtonText: "不保存",
          cancelButtonClass: "cancel-button",
          type: "warning",
        });
        if (action === "confirm") {
          await this.saveProject();
        }
      } catch {}
      this.$emit("close");
    },
    
    // 导出项目数据到Excel
    async exportProject() {
      try {
        if (!this.projectName) {
          this.$toast.warning("请先填写项目名称");
          return;
        }
        
        this.$toast.info("正在导出Excel，请稍候...");
        
        // 保存当前激活的组件
        const currentComponent = this.activeComponent;
        
        // 强制切换到UncertaintyAnalysis组件，确保图表可见并可渲染
        this.switchComponent("UncertaintyAnalysis");
        
        // 等待组件渲染完成
        await new Promise(resolve => this.$nextTick(resolve));
        
        // 再等待一段时间，确保所有图表都完全渲染
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // 获取图表实例
        let breakevenChart = null;
        let sensitivityChart = null;
        
        if (this.$refs.dynamicComponent) {
          if (typeof this.$refs.dynamicComponent.getBreakevenChart === 'function') {
            breakevenChart = this.$refs.dynamicComponent.getBreakevenChart();
            sensitivityChart = this.$refs.dynamicComponent.getSensitivityChart();
            console.log("从组件获取到的图表实例:", {
              breakevenChart: !!breakevenChart,
              sensitivityChart: !!sensitivityChart
            });
          } else {
            // 尝试直接从组件的子组件获取
            breakevenChart = this.$refs.dynamicComponent.$refs?.breakevenRef?.breakevenChart;
            sensitivityChart = this.$refs.dynamicComponent.$refs?.sensitivityRef?.sensitivityChart;
            console.log("直接从子组件获取到的图表实例:", {
              breakevenChart: !!breakevenChart,
              sensitivityChart: !!sensitivityChart
            });
          }
        }
        
        // 获取图表的Base64数据
        let breakevenChartBase64 = null;
        let sensitivityChartBase64 = null;
        
        // 确保即使图表实例即将被销毁，也要获取Base64数据
        try {
          if (breakevenChart && typeof breakevenChart.getDataURL === 'function') {
            breakevenChartBase64 = breakevenChart.getDataURL({
              pixelRatio: 2,
              backgroundColor: "#fff",
              excludeComponents: ['toolbox']
            });
            console.log("获取到盈亏平衡分析图Base64数据");
          }
          
          if (sensitivityChart && typeof sensitivityChart.getDataURL === 'function') {
            sensitivityChartBase64 = sensitivityChart.getDataURL({
              pixelRatio: 2,
              backgroundColor: "#fff",
              excludeComponents: ['toolbox']
            });
            console.log("获取到敏感性分析图Base64数据");
          }
        } catch (getDataURLError) {
          console.error("获取图表Base64数据时发生错误:", getDataURLError);
          // 即使发生错误，也继续导出，只是没有图表
        }
        
        // 恢复原来的组件
        this.switchComponent(currentComponent);
        
        // 调用ExcelExportUtil导出项目数据，传递Base64数据
        const success = await ExcelExportUtil.exportProject(
          this.$store.state.projectData,
          this.$toast,
          { 
            breakevenChart: null, // 不再需要图表实例
            sensitivityChart: null, // 不再需要图表实例
            breakevenChartBase64,
            sensitivityChartBase64
          }
        );
        
        if (success) {
          this.$toast.success("导出成功");
        }
      } catch (error) {
        console.error("导出Excel失败:", error);
        this.$toast.error("导出失败：" + error.message);
      }
    },
  },
};
</script>

<style scoped>
.jindex-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.top-nav {
  background-color: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.nav-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.action-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
}

.file-name-display {
  font-size: 12px;
  color: #909399;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  margin-right: 15px;
}

.nav-btn {
  min-width: 120px;
}

.nav-btn.active {
  background-color: #409eff;
  color: white;
}

.component-container {
  flex: 1;
  padding: 20px;
  overflow: auto;
}

.cancel-button {
  background-color: #f56c6c !important;
  border-color: #f56c6c !important;
  color: white !important;
}

.cancel-button:hover {
  background-color: #f78989 !important;
  border-color: #f78989 !important;
  color: white !important;
}
</style>
